import { useEffect, useRef, useState } from "react";
import { Award, Leaf, Settings, Truck, Shield, Recycle, Clock, Sparkles } from "lucide-react";

const features = [
  { icon: Award, title: "Premium Quality", desc: "Rigorous quality control meeting international standards for every product.", gradient: "from-amber-400 to-orange-500" },
  { icon: Leaf, title: "Eco-Friendly", desc: "100% natural and biodegradable products, kind to the environment.", gradient: "from-green-400 to-emerald-500" },
  { icon: Settings, title: "Custom Solutions", desc: "Tailored specifications and packaging to meet your exact needs.", gradient: "from-purple-400 to-violet-500" },
  { icon: Truck, title: "Timely Delivery", desc: "Reliable delivery options available across India and beyond.", gradient: "from-orange-400 to-red-500" },
  { icon: Shield, title: "Quality Assurance", desc: "Strict quality checks at every stage of production.", gradient: "from-red-400 to-rose-500" },
  { icon: Recycle, title: "Zero Waste", desc: "Every part of the coconut husk is utilized efficiently.", gradient: "from-teal-400 to-cyan-500" },
  { icon: Clock, title: "Great Support", desc: "Dedicated support Mon-Sat, 9am to 6pm IST.", gradient: "from-indigo-400 to-blue-500" },
  { icon: Sparkles, title: "Best Prices", desc: "Competitive pricing without compromising on quality.", gradient: "from-pink-400 to-fuchsia-500" },
];

export default function Features() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-padding bg-navy relative overflow-hidden" data-testid="features-section">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-white to-transparent rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-coir to-transparent rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
      </div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
      </div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gradient-to-r from-transparent to-coir" />
            <span className="text-coir font-semibold text-sm uppercase tracking-[0.2em] font-sans">Why Choose Us</span>
            <div className="w-12 h-[2px] bg-gradient-to-l from-transparent to-coir" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6 tracking-tight">
            The <span className="text-coir">Coconut Fiber</span> & <span className="text-forest">Coco Peat</span> Advantage
          </h2>
          <p className="text-white/60 text-lg font-sans leading-relaxed">
            Traditional expertise meets modern technology for premium coir products that exceed expectations.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div 
              key={i} 
              className={`group bg-white/5 backdrop-blur-sm p-6 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:-translate-y-2 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} 
              style={{ transitionDelay: `${i * 80}ms` }} 
              data-testid={`feature-card-${i}`}
            >
              {/* Icon with gradient background */}
              <div className={`w-14 h-14 bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 group-hover:shadow-xl transition-all duration-500`}>
                <f.icon className="w-7 h-7 text-white" />
              </div>
              
              <h3 className="text-xl font-serif text-white mb-3 tracking-tight group-hover:text-coir transition-colors duration-300">
                {f.title}
              </h3>
              
              <p className="text-white/50 text-sm font-sans leading-relaxed group-hover:text-white/70 transition-colors duration-300">
                {f.desc}
              </p>
              
              {/* Hover accent line */}
              <div className={`h-0.5 bg-gradient-to-r ${f.gradient} mt-5 w-0 group-hover:w-full transition-all duration-500`} />
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className={`text-center mt-16 transition-all duration-700 delay-700 ${vis ? "opacity-100" : "opacity-0"}`}>
          <p className="text-white/60 mb-6 font-sans text-lg">Ready to experience the difference?</p>
          <button 
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} 
            className="btn-accent group"
            data-testid="request-quote-btn"
          >
            Request a Quote
          </button>
        </div>
      </div>
    </section>
  );
}
